import React from 'react';
import { findIndex, differenceBy, map } from 'lodash';
import { TYPES } from 'constants/config';
import request from 'utils/request_ajax';
import RelatedUrlMapping from './RelatedUrlMapping';
import ExternalLink from './ExternalLink';
import WebsiteURL from './WebsiteURL';
import EditingMapping from './EditingMapping';

class RelatedUrlsTableRow extends React.Component {
  state = {
    editing: this.props.editing,
    data: this.props.relatedUrl,
    errorMessage: null,
    relatedContentType: null,
  };

  learningContent = 'LearningContent';

  componentDidMount() {
    this.setRelatedContentType();
  }

  editRelatedUrl = () => {
    this.originalData = this.state.data;
    this.setState({
      editing: true,
    });
  };

  cancelEditRelatedUrl = () => {
    if (this.props.hideNewMappingForm) {
      this.props.hideNewMappingForm();
    }

    this.setState({
      editing: false,
      data: this.originalData,
      errorMessage: null,
      relatedContentType: null,
    });
  };

  saveRelatedUrl = e => {
    e.preventDefault();
    if (this.state.data.id) {
      this.props
        .updateRelatedUrl(this.state.data)
        .then(data => {
          this.setState({ editing: false, data, relatedContentType: null });
          if (data.url) {
            lly.relatedUrls.addOrUpdate(data);
          } else if (data.name) {
            lly.relatedKeywords.addOrUpdate(data);
          }
        })
        .catch(data => {
          this.setState({ errorMessage: data.error });
        });
    } else {
      request
        .post(this.props.path, this.state.data)
        .then(data => {
          this.props.saveNewRow(data);
          if (this.props.hideNewMappingForm) {
            this.props.hideNewMappingForm();
          }
          if (data.url) {
            lly.relatedUrls.addOrUpdate(data);
          } else if (data.name) {
            lly.relatedKeywords.addOrUpdate(data);
          }
        })
        .fail(response => {
          this.setState({ errorMessage: response.responseJSON.error });
        });
    }
  };

  delete = () => {
    const deletedUrl = this.state.data;
    this.props.deleteRelatedUrl(deletedUrl);
  };

  addNewMappingFromSuggestion = suggestion => {
    const mapping = {
      learningContentTitle: suggestion.title,
      learningContentType: suggestion.type,
      learningContentId: suggestion.id,
      lessonsCount: suggestion.meta.lessonsCount,
      selectedSection: null,
    };
    this.addNewMapping(mapping);
  };

  addNewExternalLinkMapping = externalLink => {
    const mapping = {
      ...externalLink,
      learningContentType: TYPES.EXTERNAL_LINK,
    };
    this.addNewMapping(mapping);
  };

  addNewMapping = mapping => {
    this.setState(
      state => ({
        data: {
          ...state.data,
          mappings: [...state.data.mappings, mapping],
        },
      }),
      this.setRelatedContentType,
    );
  };

  setRelatedContentType = (relatedContentType = null) => {
    if (!this.props.features.chrome_extension_link_to_external_urls) {
      relatedContentType = this.learningContent;
    }
    this.setState({ relatedContentType });
  };

  findMappingIndex(mapping, updatedExternalLinkField) {
    let mappingParams;

    if (
      mapping.learningContentType === TYPES.EXTERNAL_LINK &&
      !mapping.learningContentId
    ) {
      mappingParams = {
        learningContentType: TYPES.EXTERNAL_LINK,
      };

      const fields = ['url', 'title', 'description'];

      fields
        .filter(field => field !== updatedExternalLinkField)
        .forEach(field => {
          mappingParams[field] = mapping[field];
        });
    } else {
      mappingParams = {
        learningContentId: mapping.learningContentId,
        learningContentType: mapping.learningContentType,
      };
    }
    return findIndex(this.state.data.mappings, mappingParams);
  }

  updateMapping = (mapping, updatedExternalLinkField = null) => {
    const { mappings } = this.state.data;
    const index = this.findMappingIndex(mapping, updatedExternalLinkField);
    const updatedMappingsArray = [
      ...mappings.slice(0, index),
      mapping,
      ...mappings.slice(index + 1),
    ];
    this.setState(state => ({
      data: { ...state.data, mappings: updatedMappingsArray },
    }));
  };

  deleteMapping = mapping => {
    const { mappings } = this.state.data;
    const index = this.findMappingIndex(mapping);
    const updatedMappingsArray = [
      ...mappings.slice(0, index),
      ...mappings.slice(index + 1),
    ];
    this.setState(state => ({
      data: { ...state.data, mappings: updatedMappingsArray },
    }));
  };

  mappingToLearningContent(mapping) {
    return {
      id: mapping.learningContentId,
      type: mapping.learningContentType,
    };
  }

  unselectedLearningContent = () => {
    const mappedLearningContent = map(
      this.state.data.mappings,
      this.mappingToLearningContent,
    );
    return differenceBy(
      this.props.learningContent,
      mappedLearningContent,
      learningContent => `${learningContent.type}${learningContent.id}`,
    );
  };

  // Sets state for changes to any inputs of the WebsiteURL component.
  handleWebsiteURLInput = (name, value) => {
    this.setState(state => ({
      data: {
        ...state.data,
        [name]: value,
      },
    }));
  };

  render() {
    const relatedUrl = this.state.data;
    const { i18n, types, param, features } = this.props;
    const canUseOnPageContext =
      param === 'url' && features.chrome_extension_on_page_context_mapping;

    const relatedUrlMapping = (mapping, index) => {
      if (
        mapping.learningContentType === TYPES.EXTERNAL_LINK &&
        !mapping.learningContentId
      ) {
        return (
          <ExternalLink
            i18n={i18n}
            key={index}
            updating
            externalLink={mapping}
            updateMapping={this.updateMapping}
            deleteMapping={this.deleteMapping}
            setRelatedContentType={this.setRelatedContentType}
          />
        );
      }

      return (
        <RelatedUrlMapping
          i18n={i18n}
          editing={this.state.editing}
          mapping={mapping}
          key={index}
          deleteMapping={this.deleteMapping}
          updateMapping={this.updateMapping}
          learningContent={this.props.learningContent}
          canUseOnPageContext={canUseOnPageContext}
        />
      );
    };

    const editingMapping = (
      <EditingMapping
        i18n={i18n}
        relatedContentType={this.state.relatedContentType}
        relatedUrl={relatedUrl}
        param={param}
        addNewExternalLinkMapping={this.addNewExternalLinkMapping}
        setRelatedContentType={this.setRelatedContentType}
        unselectedLearningContent={this.unselectedLearningContent()}
        addNewMappingFromSuggestion={this.addNewMappingFromSuggestion}
        cancelEditRelatedUrl={this.cancelEditRelatedUrl}
        learningContent={this.learningContent}
      />
    );

    return (
      <form className="FlexboxTable-row" onSubmit={this.saveRelatedUrl}>
        <WebsiteURL
          editing={this.state.editing}
          relatedUrl={relatedUrl}
          param={param}
          i18n={i18n}
          errorMessage={this.state.errorMessage}
          types={types}
          handleWebsiteURLInput={this.handleWebsiteURLInput}
          deleteURL={this.delete}
          editRelatedUrl={this.editRelatedUrl}
          setRef={this.setRef}
          canUseOnPageContext={canUseOnPageContext}
        />
        <div className="FlexboxTable-cell FlexboxTable-cell--containsRows d-f fd-c jc-sb">
          <div>
            {relatedUrl.mappings &&
              relatedUrl.mappings.map((mapping, index) =>
                relatedUrlMapping(mapping, index),
              )}
          </div>
          {this.state.editing && editingMapping}
        </div>
      </form>
    );
  }
}

export default RelatedUrlsTableRow;
